import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/*
 * Theme state for the whole frontend.
 *
 * There are two distinct values and conflating them is the usual bug:
 *
 *   preference — what the user chose: 'system' | 'light' | 'dark'. Persisted.
 *   theme      — what is actually on screen: 'light' | 'dark'. Derived.
 *
 * `theme` is mirrored onto <html data-theme="…">, which is the hook every color
 * token in tailwind.config.js hangs off. The same resolution runs in an inline
 * script in index.html so the first paint is already correct — keep the two in
 * sync if you change the storage key or the default.
 */

export const THEME_STORAGE_KEY = 'bluebear.theme';

const PREFERENCES = ['system', 'light', 'dark'];
const DARK_QUERY = '(prefers-color-scheme: dark)';

const readStoredPreference = () => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return PREFERENCES.includes(stored) ? stored : 'light';
  } catch {
    // Private mode / storage disabled — use the product default.
    return 'light';
  }
};

const systemTheme = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'dark';
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
};

export const resolveTheme = (preference) => (preference === 'system' ? systemTheme() : preference);

const ThemeContext = createContext({
  theme: 'light',
  preference: 'light',
  setPreference: () => {},
  toggleTheme: () => {},
  isDark: false,
});

export function ThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(() =>
    typeof window === 'undefined' ? 'light' : readStoredPreference()
  );
  const [systemValue, setSystemValue] = useState(systemTheme);

  const theme = preference === 'system' ? systemValue : preference;

  // Track the OS setting so 'system' stays live while the tab is open.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia(DARK_QUERY);
    const onChange = (event) => setSystemValue(event.matches ? 'dark' : 'light');
    // Safari < 14 only has the deprecated listener API.
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    }
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    root.style.colorScheme = theme;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme === 'dark' ? '#030712' : '#ffffff');
  }, [theme]);

  const setPreference = useCallback((next) => {
    const value = PREFERENCES.includes(next) ? next : 'light';
    setPreferenceState(value);
    try {
      // 'system' is stored explicitly: an absent key now means the 'light'
      // default, so removing the key would forget a deliberate 'system' choice.
      window.localStorage.setItem(THEME_STORAGE_KEY, value);
    } catch {
      // Not persisting is survivable; the in-memory choice still applies.
    }
  }, []);

  // Flips to the opposite of what is on screen, and pins it — a user reaching
  // for the toggle wants that specific look, not "follow the OS again".
  const toggleTheme = useCallback(() => {
    setPreference(theme === 'dark' ? 'light' : 'dark');
  }, [setPreference, theme]);

  const value = useMemo(
    () => ({ theme, preference, setPreference, toggleTheme, isDark: theme === 'dark' }),
    [preference, setPreference, theme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}

export default ThemeContext;
